import { Suspense, forwardRef, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  Button,
  Center,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
  theme,
} from '@squire/ui';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { CashAppQrCode } from '@/components/booking/CashAppQrCode';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { StripeProvider } from '@/providers/StripeProvider';
import composeRefs from '@/utils/composeRefs';
import type { PaymentMethodCashApp } from '@/utils/paymentMethods';

import { CashAppQrCodeSkeleton } from '../CashAppQrCodeSkeleton';
import { styles } from './styles';
import type { CashAppBottomSheetProps } from './types';

const CashAppBottomSheet = forwardRef<BottomSheetRef, CashAppBottomSheetProps>(
  (
    {
      onDismiss,
      testID = 'cash-app-bottom-sheet',
      onPaymentSuccess,
      customer,
      cartParams,
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('CashAppBottomSheet');

    const internalRef = useRef<BottomSheetRef>(null);

    const handleDismiss = () => {
      internalRef.current?.close();
      onDismiss?.();
    };

    const handlePaymentSuccess = (data: PaymentMethodCashApp) => {
      internalRef.current?.close();
      onPaymentSuccess?.(data);
    };

    const handlePaymentError = () => {
      internalRef.current?.close();
    };

    return (
      <BottomSheet
        accessibilityLabel={t('title')}
        hideHandle
        scrollable
        testID={testID}
        onDismiss={onDismiss}
        containerStyle={styles.container}
        ref={composeRefs(ref, internalRef)}
      >
        <Suspense fallback={<Spinner />}>
          <StripeProvider>
            <VStack style={styles.content} space={theme.spacing[5]}>
              <HStack
                centerVertical
                style={styles.header}
                paddingVertical={theme.spacing[5]}
              >
                <Heading variant="m">{t('title')}</Heading>

                <CloseButton
                  onPress={handleDismiss}
                  accessibilityLabel={t('accessibility.closeButton')}
                  accessibilityHint={t('accessibility.closeButtonHint')}
                />
              </HStack>

              <Suspense fallback={<CashAppQrCodeSkeleton />}>
                <ErrorBoundary
                  FallbackComponent={({ resetErrorBoundary }) => (
                    <Center style={styles.errorContainer}>
                      <Text variant="m" style={styles.errorText}>
                        {t('error.loadingQRCode')}
                      </Text>

                      <Button onPress={resetErrorBoundary}>{t('retry')}</Button>
                    </Center>
                  )}
                >
                  <CashAppQrCode
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    customer={customer}
                    cartParams={cartParams}
                  />
                </ErrorBoundary>
              </Suspense>
            </VStack>
          </StripeProvider>
        </Suspense>
      </BottomSheet>
    );
  },
);

CashAppBottomSheet.displayName = 'CashAppBottomSheet';
export { CashAppBottomSheet };
